import React, { useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import '../assets/css/libs.css'
import '../assets/css/style.css'
import minus from '../assets/img/icon/minus.svg'
import times from '../assets/img/icon/times_2.svg'
import image1 from '../assets/img/rougo_simulation/image_1.svg'
import { Footer } from '../components/footer'
import { Header } from '../components/header'
import { useDealerCodeUrl } from '../hook/useDealerCode'
import { useDescription } from '../hook/useDescription'
import { useHeadTitle } from '../hook/useHeadTitle'
import { commify, fixedNumber, getDecimalPointLength, validateIsNumber } from '../js/utils'

const defaultValues = {
  monthlyOutgo: '',
  monthlyIncome: '',
  monthlyShortage: '',
  shortage: '6.0',
  year: '30',
  sumShortage: '',
}

const constraintValues = {
  monthlyOutgo: {
    upper: 999.9,
    lower: 0,
    decimalPoint: 1,
  },
  monthlyIncome: {
    upper: 999.9,
    lower: 0,
    decimalPoint: 1,
  },
  monthlyShortage: {
    decimalPoint: 1,
  },
  shortage: {
    upper: 999.9,
    lower: -999.9,
    decimalPoint: 1,
  },
  year: {
    upper: 99,
    lower: 0,
    decimalPoint: 0,
  },
  sumShortage: {
    decimalPoint: 1,
  },
}

export const RougoSimulation: React.FC = () => {
  const [monthlyOutgo, setMonthlyOutgo] = useState(defaultValues.monthlyOutgo)
  const [monthlyIncome, setMonthlyIncome] = useState(defaultValues.monthlyIncome)
  const [monthlyShortage, setMonthlyShortage] = useState(defaultValues.monthlyShortage)
  const [shortage, setShortage] = useState(defaultValues.shortage)
  const [year, setYear] = useState(defaultValues.year)
  const [sumShortage, setSumShortage] = useState(defaultValues.sumShortage)
  // TODO React Hook Form で置き換える

  const calcedMonthlyShortage = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      const income = Number(monthlyIncome)
      const outgo = Number(monthlyOutgo)
      if (isNaN(income) || isNaN(outgo)) {
        setMonthlyShortage('')
        setShortage('')
      } else {
        const diff = outgo - income
        setMonthlyShortage(fixedNumber(diff, constraintValues.monthlyShortage.decimalPoint))
        setShortage(fixedNumber(diff, constraintValues.shortage.decimalPoint))
        setYear(defaultValues.year)
        setSumShortage(defaultValues.sumShortage)
      }
    },
    [monthlyIncome, monthlyOutgo]
  )

  const calcedSumShortage = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      const shortageValue = Number(shortage)
      const yearValue = Number(year)
      if (isNaN(shortageValue) || isNaN(yearValue)) {
        setSumShortage('')
      } else {
        const sum = shortageValue * yearValue * 12
        setSumShortage(commify(fixedNumber(sum, constraintValues.sumShortage.decimalPoint)))
      }
    },
    [shortage, year]
  )
  useHeadTitle('今こそ資産運用を考えよう', '老後資金シミュレーション')
  useDescription(
    'シミュレーションを通じてご自身の資産運用を考えてみませんか。野村アセットマネジメントが資産運用に取り組む皆さまをサポートします。'
  )

  const handleBlur = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement>,
      setValue: React.Dispatch<React.SetStateAction<string>>,
      upper: number,
      lower: number,
      decimalPoint: number
    ) => {
      const inputValue = e.target.value
      if (inputValue === '') {
        setValue('')
        return
      }
      if (!validateIsNumber(inputValue)) {
        setValue('')
        return
      }
      // 小数を任意の桁で丸める為、値の範囲チェックにValidityState.validを用いない
      if (Number(inputValue) > upper) {
        setValue('')
        return
      }
      if (Number(inputValue) < lower) {
        setValue('')
        return
      }
      if (getDecimalPointLength(inputValue) > decimalPoint) {
        if (decimalPoint === 0) {
          setValue(String(Math.round(Number(inputValue))))
          return
        } else {
          setValue(fixedNumber(inputValue, decimalPoint))
          return
        }
      }
      if (inputValue.match(/^[+-]?0/) !== null) {
        setValue(String(Number(inputValue)))
      }
    },
    []
  )

  const handleUpDownKey = useCallback(
    (
      e: React.KeyboardEvent<HTMLInputElement>,
      setValue: React.Dispatch<React.SetStateAction<string>>,
      value: string,
      step: number,
      upper: number,
      lower: number,
      decimalPoint: number
    ) => {
      const nowValue = value !== undefined ? Number(value) : Number('')

      if (e.key === 'ArrowUp') {
        const calcValue = nowValue + step
        const newValue = calcValue <= upper ? Number(fixedNumber(calcValue, decimalPoint)) : upper
        setValue(String(newValue))
        e.preventDefault()
        return
      } else if (e.key === 'ArrowDown') {
        const calcValue = nowValue - step
        const newValue = calcValue >= lower ? Number(fixedNumber(calcValue, decimalPoint)) : lower
        setValue(String(newValue))
        e.preventDefault()
        return
      }
    },
    []
  )

  return (
    <React.Fragment>
      <Header />
      <body className="include rougo">
        <main>
          <section>
            <div className="c-title-wrap">
              <h2 className="c-title--primary">老後資金シミュレーション</h2>
              <p className="c-title__lead">セカンドライフに必要な資金をイメージ</p>
            </div>
            <div id="app-root" role="application">
              <div className="u-bg--white">
                <div className="l-container">
                  <div className="p-simulation">
                    <div className="u-mb30px">
                      <p className="u-text--center">
                        <img
                          src={image1}
                          alt="不足額は金融資産等からまかなうことになります。必要となる老後資金を計算してみましょう。"
                        />
                      </p>
                    </div>

                    <form
                      className="c-grid--align-items-center u-mt30px c-grid--wrap"
                      onSubmit={(e) => calcedSumShortage(e)}
                    >
                      <div className="c-grid__col u-size--w200px">
                        <span className="p-calc__title">不足総額を計算</span>
                      </div>
                      <div className="c-grid--align-items-center">
                        <div className="c-grid__col u-size--w204px">
                          <div className="c-form-group u-xl-ml0 u-md-mr0">
                            <span className="c-form-group__sup">不足額（月額）</span>
                            <span className="c-form-group__input">
                              <input
                                type="number"
                                placeholder="00"
                                step="0.1" // Math.pow()では誤差が出るので直接記述
                                max={constraintValues.shortage.upper}
                                min={constraintValues.shortage.lower}
                                className="c-form__input u-text--right u-size--w110px"
                                value={shortage}
                                onChange={(e) => setShortage(e.target.value)}
                                onKeyDown={(e) =>
                                  handleUpDownKey(
                                    e,
                                    setShortage,
                                    shortage,
                                    0.1,
                                    constraintValues.shortage.upper,
                                    constraintValues.shortage.lower,
                                    constraintValues.shortage.decimalPoint
                                  )
                                }
                                onBlurCapture={(e) =>
                                  handleBlur(
                                    e,
                                    setShortage,
                                    constraintValues.shortage.upper,
                                    constraintValues.shortage.lower,
                                    constraintValues.shortage.decimalPoint
                                  )
                                }
                              />
                              <span className="c-form-group__text">万円</span>
                            </span>
                          </div>
                        </div>
                        <div className="c-grid__col u-size--w38px">
                          <img src={times} alt="✕" className="u-mt26px" />
                        </div>
                        <div className="c-grid__col u-size--w80px u-mt12px u-text--nowrap">
                          <span className="c-form-group__text">12ヶ月</span>
                        </div>
                        <div className="c-grid__col u-size--w28px">
                          <img src={times} alt="✕" className="u-mt26px" />
                        </div>
                        <div className="c-grid__col u-size--w160px">
                          <div className="c-form-group">
                            <span className="c-form-group__sup">期間</span>
                            <span className="c-form-group__input">
                              <input
                                type="number"
                                placeholder="00"
                                step="1"
                                max={constraintValues.year.upper}
                                min={constraintValues.year.lower}
                                className="c-form__input u-text--right u-size--w70px"
                                value={year}
                                onChange={(e) => setYear(e.target.value)}
                                onKeyDown={(e) =>
                                  handleUpDownKey(
                                    e,
                                    setYear,
                                    year,
                                    1,
                                    constraintValues.year.upper,
                                    constraintValues.year.lower,
                                    constraintValues.year.decimalPoint
                                  )
                                }
                                onBlurCapture={(e) =>
                                  handleBlur(
                                    e,
                                    setYear,
                                    constraintValues.year.upper,
                                    constraintValues.year.lower,
                                    constraintValues.year.decimalPoint
                                  )
                                }
                              />
                              <span className="c-form-group__text">年</span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="c-grid--align-items-center c-glid--md-fd-column">
                        <div className="c-grid__col u-size--w160px">
                          <button className="c-button-calc">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 164 92"
                              className="spOnly"
                            >
                              <defs>
                                <filter id="a" x="0" y="0" filterUnits="userSpaceOnUse">
                                  <feOffset dx="4" dy="4"></feOffset>
                                  <feGaussianBlur result="blur"></feGaussianBlur>
                                  <feFlood floodColor="#5a435a"></feFlood>
                                  <feComposite operator="in" in2="blur"></feComposite>
                                  <feComposite in="SourceGraphic"></feComposite>
                                </filter>
                              </defs>
                              <g filter="url(#a)">
                                <g fill="#fff" className="c-button-calc__bg">
                                  <path d="M80 85.854L1.998 55.63V10.001c0-4.411 3.59-8 8.002-8h140.002c4.412 0 8 3.589 8 8V55.63L80 85.854z"></path>
                                  <path
                                    d="M80 83.709l76.002-29.449V10.001c0-3.308-2.691-6-6-6H10a6.008 6.008 0 00-6.002 6V54.26L80 83.71m0 4.29L-.002 57V10.001c0-5.524 4.477-10 10.002-10h140.002c5.52 0 10 4.476 10 10V57L80 87.998z"
                                    fill="#62a598"
                                  ></path>
                                </g>
                              </g>
                            </svg>

                            <span className="spOnly">計算する</span>
                          </button>
                          <button className="c-button-calc" type="submit" id="btn_shortageMonthly">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="138"
                              height="74"
                              className="pcOnly"
                              viewBox="0 0 138 74"
                            >
                              <g style={{ isolation: 'isolate' }}>
                                <g mix-blend-mode="multiply">
                                  <path
                                    fill="#5a435a"
                                    d="M137,39,106,74H14A10,10,0,0,1,4,64V14A10,10,0,0,1,14,4h92Z"
                                  />
                                  <path
                                    fill="#5a435a"
                                    d="M104.2,8l27.5,31L104.2,70H14a6,6,0,0,1-6-6V14a6,6,0,0,1,6-6h90.2M106,4H14A10,10,0,0,0,4,14V64A10,10,0,0,0,14,74h92l31-35L106,4Z"
                                  />
                                </g>
                                <path
                                  fill="#fff"
                                  className="c-button-calc__bg"
                                  d="M133,35,102,70H10A10,10,0,0,1,0,60V10A10,10,0,0,1,10,0h92Z"
                                />
                                <path
                                  fill="#62a598"
                                  d="M100.2,4l27.5,31L100.2,66H10a6,6,0,0,1-6-6V10a6,6,0,0,1,6-6h90.2M102,0H10A10,10,0,0,0,0,10V60A10,10,0,0,0,10,70h92l31-35L102,0Z"
                                />
                                <text
                                  fontSize="20px"
                                  fill="#5a435a"
                                  style={{ isolation: 'isolate' }}
                                  transform="translate(19.9 41)"
                                >
                                  計算する
                                </text>
                              </g>
                            </svg>
                          </button>
                        </div>
                        <div className="c-grid__col">
                          <div className="c-form-group">
                            <span className="c-form-group__sup">不足総額</span>
                            <span className="c-form-group__input">
                              <input
                                type="text"
                                placeholder="- - - - - - - - -"
                                className="c-form__output u-size--w215px"
                                value={sumShortage}
                                readOnly
                              />
                              <span className="c-form-group__text--large">万円</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </form>

                    <form
                      className="c-grid--align-items-center u-mt30px c-grid--wrap"
                      onSubmit={(e) => calcedMonthlyShortage(e)}
                    >
                      <div className="c-grid__col u-size--w200px">
                        <span className="p-calc__title">不足額を計算</span>
                      </div>
                      <div className="c-grid--align-items-center">
                        <div className="c-grid__col u-size--w246px">
                          <div className="c-form-group u-xl-ml0">
                            <span className="c-form-group__sup">
                              生活費を含めた
                              <br className="spOnly" />
                              支出（月額）
                            </span>
                            <span className="c-form-group__input">
                              <input
                                type="number"
                                placeholder="00"
                                step="0.1" // Math.pow()では誤差が出るので直接記述
                                max={constraintValues.monthlyOutgo.upper}
                                min={constraintValues.monthlyOutgo.lower}
                                value={monthlyOutgo}
                                onChange={(e) => setMonthlyOutgo(e.target.value)}
                                onKeyDown={(e) =>
                                  handleUpDownKey(
                                    e,
                                    setMonthlyOutgo,
                                    monthlyOutgo,
                                    0.1,
                                    constraintValues.monthlyOutgo.upper,
                                    constraintValues.monthlyOutgo.lower,
                                    constraintValues.monthlyOutgo.decimalPoint
                                  )
                                }
                                onBlurCapture={(e) =>
                                  handleBlur(
                                    e,
                                    setMonthlyOutgo,
                                    constraintValues.monthlyOutgo.upper,
                                    constraintValues.monthlyOutgo.lower,
                                    constraintValues.monthlyOutgo.decimalPoint
                                  )
                                }
                                className="c-form__input u-text--right u-size--w140px"
                              />
                              <span className="c-form-group__text">万円</span>
                            </span>
                          </div>
                        </div>
                        <div className="c-grid__col u-size--w44px">
                          <img src={minus} alt="−" className="u-md-mt50px" />
                        </div>
                        <div className="c-grid__col u-size--w220px">
                          <div className="c-form-group">
                            <span className="c-form-group__sup">
                              年金等の
                              <br className="spOnly" />
                              収入（月額）
                            </span>
                            <span className="c-form-group__input">
                              <input
                                type="number"
                                placeholder="00"
                                step="0.1" // Math.pow()では誤差が出るので直接記述
                                max={constraintValues.monthlyIncome.upper}
                                min={constraintValues.monthlyIncome.lower}
                                value={monthlyIncome}
                                onChange={(e) => setMonthlyIncome(e.target.value)}
                                onKeyDown={(e) =>
                                  handleUpDownKey(
                                    e,
                                    setMonthlyIncome,
                                    monthlyIncome,
                                    0.1,
                                    constraintValues.monthlyIncome.upper,
                                    constraintValues.monthlyIncome.lower,
                                    constraintValues.monthlyIncome.decimalPoint
                                  )
                                }
                                onBlurCapture={(e) =>
                                  handleBlur(
                                    e,
                                    setMonthlyIncome,
                                    constraintValues.monthlyIncome.upper,
                                    constraintValues.monthlyIncome.lower,
                                    constraintValues.monthlyIncome.decimalPoint
                                  )
                                }
                                className="c-form__input u-text--right u-size--w105px"
                              />
                              <span className="c-form-group__text">万円</span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="c-grid--align-items-center c-glid--md-fd-column">
                        <div className="c-grid__col u-size--w160px">
                          <button className="c-button-calc">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 164 92"
                              className="spOnly"
                            >
                              <defs>
                                <filter id="a" x="0" y="0" filterUnits="userSpaceOnUse">
                                  <feOffset dx="4" dy="4"></feOffset>
                                  <feGaussianBlur result="blur"></feGaussianBlur>
                                  <feFlood floodColor="#5a435a"></feFlood>
                                  <feComposite operator="in" in2="blur"></feComposite>
                                  <feComposite in="SourceGraphic"></feComposite>
                                </filter>
                              </defs>
                              <g filter="url(#a)">
                                <g fill="#fff" className="c-button-calc__bg">
                                  <path d="M80 85.854L1.998 55.63V10.001c0-4.411 3.59-8 8.002-8h140.002c4.412 0 8 3.589 8 8V55.63L80 85.854z"></path>
                                  <path
                                    d="M80 83.709l76.002-29.449V10.001c0-3.308-2.691-6-6-6H10a6.008 6.008 0 00-6.002 6V54.26L80 83.71m0 4.29L-.002 57V10.001c0-5.524 4.477-10 10.002-10h140.002c5.52 0 10 4.476 10 10V57L80 87.998z"
                                    fill="#62a598"
                                  ></path>
                                </g>
                              </g>
                            </svg>

                            <span className="spOnly">計算する</span>
                          </button>
                          <button className="c-button-calc" type="submit" id="btn_shortageMonthly">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="138"
                              height="74"
                              className="pcOnly"
                              viewBox="0 0 138 74"
                            >
                              <g style={{ isolation: 'isolate' }}>
                                <g mix-blend-mode="multiply">
                                  <path
                                    fill="#5a435a"
                                    d="M137,39,106,74H14A10,10,0,0,1,4,64V14A10,10,0,0,1,14,4h92Z"
                                  />
                                  <path
                                    fill="#5a435a"
                                    d="M104.2,8l27.5,31L104.2,70H14a6,6,0,0,1-6-6V14a6,6,0,0,1,6-6h90.2M106,4H14A10,10,0,0,0,4,14V64A10,10,0,0,0,14,74h92l31-35L106,4Z"
                                  />
                                </g>
                                <path
                                  fill="#fff"
                                  className="c-button-calc__bg"
                                  d="M133,35,102,70H10A10,10,0,0,1,0,60V10A10,10,0,0,1,10,0h92Z"
                                />
                                <path
                                  fill="#62a598"
                                  d="M100.2,4l27.5,31L100.2,66H10a6,6,0,0,1-6-6V10a6,6,0,0,1,6-6h90.2M102,0H10A10,10,0,0,0,0,10V60A10,10,0,0,0,10,70h92l31-35L102,0Z"
                                />
                                <text
                                  fontSize="20px"
                                  fill="#5a435a"
                                  style={{ isolation: 'isolate' }}
                                  transform="translate(19.9 41)"
                                >
                                  計算する
                                </text>
                              </g>
                            </svg>
                          </button>
                        </div>
                        <div className="c-grid__col">
                          <div className="c-form-group">
                            <span className="c-form-group__sup">不足額（月額）</span>
                            <span className="c-form-group__input">
                              <input
                                type="text"
                                placeholder="- - - - - - - - -"
                                className="c-form__output u-size--w215px"
                                value={monthlyShortage}
                                readOnly
                              />
                              <span className="c-form-group__text--large">万円</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </form>

                    <div className="u-mt30px">
                      <p className="c-note">
                        {/* eslint-disable */}※
                        金融ジェロントロジーにおける資産運用に関する調査（2017年11月:野村アセットマネジメント・野村資本市場研究所）
                        {/* eslint-enable */}
                      </p>
                    </div>

                    <div className="u-mt70px c-grid-button u-text--center">
                      <Link
                        to={useDealerCodeUrl('/reversal')}
                        className="c-button--secondary c-button-rectangle u-size--w30 u-mr50px"
                      >
                        <span className="c-button__text--m">取り崩しシミュレーション</span>
                        <br />
                        <span className="c-button__text--xs pcOnly">
                          取り崩し額によって
                          <br />
                          資産寿命はどう変わる？
                        </span>
                      </Link>
                      <Link
                        to={useDealerCodeUrl('/calc')}
                        className="c-button--secondary c-button-rectangle u-size--w30 u-mr50px"
                      >
                        <span className="c-button__text--m">つみたてシミュレーション</span>
                        <br />
                        <span className="c-button__text--xs pcOnly">
                          金額や期間などを決めて色々な
                          <br />
                          パターンの「つみたて」を計算
                        </span>
                      </Link>
                      <Link
                        to={useDealerCodeUrl('/simulation')}
                        className="c-button--secondary c-button-rectangle u-size--w30"
                      >
                        <span className="c-button__text--m">将来シミュレーション</span>
                        <br />
                        <span className="c-button__text--xs pcOnly">
                          あなたの資産の将来を
                          <br />
                          預金と比べてみましょう
                        </span>
                      </Link>
                    </div>

                    <div className="c-button-group">
                      <Link
                        to={useDealerCodeUrl('/')}
                        className="c-button--primary c-button-arrow--back"
                      >
                        <span className="c-button__text--xs">TOPページへ戻る</span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </body>
    </React.Fragment>
  )
}
